import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { XIcon } from '../../../components/svgs/icons/XIcon';
import { s } from './styles';

export interface ImageAttachment {
	id: string;
	file: File;
	previewUrl: string;
	name: string;
	size: number;
}

interface ImageAttachmentManagerProps {
	images: ImageAttachment[];
	onImagesChange: (images: ImageAttachment[]) => void;
	maxImages?: number;
	maxSizeBytes?: number;
	acceptedTypes?: string[];
	disabled?: boolean;
	fileInputRef?: React.RefObject<HTMLInputElement>;
}

const DEFAULT_MAX_IMAGES = 2;
const DEFAULT_MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const DEFAULT_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

export const ImageAttachmentManager = ({
	images,
	onImagesChange,
	maxImages = DEFAULT_MAX_IMAGES,
	maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
	acceptedTypes = DEFAULT_ACCEPTED_TYPES,
	disabled = false,
	fileInputRef: externalFileInputRef,
}: ImageAttachmentManagerProps) => {
	const internalFileInputRef = React.useRef<HTMLInputElement>(null);
	const fileInputRef = externalFileInputRef || internalFileInputRef;
	const [isDragging, setIsDragging] = React.useState(false);

	const validateAndAddImage = React.useCallback(
		(file: File) => {
			// Check if we've reached max images
			if (images.length >= maxImages) {
				toast.error(`Maximum ${maxImages} images allowed`);
				return;
			}

			// Check file type
			if (!acceptedTypes.includes(file.type)) {
				toast.error(`Invalid file type. Accepted types: ${acceptedTypes.join(', ')}`);
				return;
			}

			// Check file size
			if (file.size > maxSizeBytes) {
				const maxSizeMB = (maxSizeBytes / (1024 * 1024)).toFixed(1);
				toast.error(`File size exceeds ${maxSizeMB}MB limit`);
				return;
			}

			// Create preview URL
			const previewUrl = URL.createObjectURL(file);

			// Add to images
			const newImage: ImageAttachment = {
				id: crypto.randomUUID(),
				file,
				previewUrl,
				name: file.name,
				size: file.size,
			};

			onImagesChange([...images, newImage]);
		},
		[images, maxImages, maxSizeBytes, acceptedTypes, onImagesChange]
	);

	const handleRemoveImage = React.useCallback(
		(imageId: string) => {
			const imageToRemove = images.find(img => img.id === imageId);
			if (imageToRemove) {
				URL.revokeObjectURL(imageToRemove.previewUrl);
			}
			onImagesChange(images.filter(img => img.id !== imageId));
		},
		[images, onImagesChange]
	);

	// Handle paste
	React.useEffect(() => {
		if (disabled) {
			return;
		}

		const handlePaste = (e: ClipboardEvent) => {
			const items = e.clipboardData?.items;
			if (!items) {
				return;
			}

			for (const item of items) {
				if (item.type.indexOf('image') !== -1) {
					const file = item.getAsFile();
					if (file) {
						e.preventDefault();
						validateAndAddImage(file);
					}
				}
			}
		};

		document.addEventListener('paste', handlePaste);
		return () => {
			document.removeEventListener('paste', handlePaste);
		};
	}, [disabled, validateAndAddImage]);

	// Handle drag and drop
	const handleDragEnter = React.useCallback(
		(e: React.DragEvent) => {
			if (disabled) {
				return;
			}
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(true);
		},
		[disabled]
	);

	const handleDragLeave = React.useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	}, []);

	const handleDragOver = React.useCallback(
		(e: React.DragEvent) => {
			if (disabled) {
				return;
			}
			e.preventDefault();
			e.stopPropagation();
		},
		[disabled]
	);

	const handleDrop = React.useCallback(
		(e: React.DragEvent) => {
			if (disabled) {
				return;
			}
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(false);

			const files = Array.from(e.dataTransfer.files);
			files.forEach(file => {
				if (file.type.startsWith('image/')) {
					validateAndAddImage(file);
				}
			});
		},
		[disabled, validateAndAddImage]
	);

	// Handle file input
	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		files.forEach(file => validateAndAddImage(file));
		// Reset input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleBrowseClick = () => {
		if (!disabled) {
			fileInputRef.current?.click();
		}
	};

	// Cleanup on unmount
	React.useEffect(() => {
		return () => {
			images.forEach(img => URL.revokeObjectURL(img.previewUrl));
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const showDropZone = images.length === 0 && !disabled;

	return (
		<div className={css(s.container)}>
			<input
				ref={fileInputRef}
				type='file'
				accept={acceptedTypes.join(',')}
				multiple={maxImages > 1}
				onChange={handleFileInputChange}
				className={css(s.hiddenInput)}
				data-testid='file-input'
				disabled={disabled}
			/>

			{showDropZone && (
				<div
					className={css(s.dropZone, isDragging && s.dropZoneDragging)}
					onDragEnter={handleDragEnter}
					onDragLeave={handleDragLeave}
					onDragOver={handleDragOver}
					onDrop={handleDrop}
					onClick={handleBrowseClick}
				>
					<div className={css(s.dropZoneContent)}>
						<span className={css(s.dropZoneText)}>Drop images here, paste, or click to browse</span>
						<span className={css(s.dropZoneHint)}>
							Max {maxImages} images • {(maxSizeBytes / (1024 * 1024)).toFixed(0)}MB per image
						</span>
					</div>
				</div>
			)}

			{images.length > 0 && (
				<div className={css(s.thumbnailContainer)}>
					{images.map(image => (
						<div key={image.id} className={css(s.thumbnail)}>
							<img src={image.previewUrl} alt={image.name} className={css(s.thumbnailImage)} />
							<button
								type='button'
								className={css(s.removeButton)}
								onClick={() => handleRemoveImage(image.id)}
								disabled={disabled}
								aria-label={`Remove ${image.name}`}
							>
								<XIcon className={css(s.removeIcon)} />
							</button>
							<div className={css(s.thumbnailInfo)}>
								<span className={css(s.thumbnailName)}>{image.name}</span>
								<span className={css(s.thumbnailSize)}>{(image.size / 1024).toFixed(1)} KB</span>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
